import React, { useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete, Download, FindInPage } from '@mui/icons-material';

import {
  ILegalClientDocumentTypeEntry,
  ILegalClientDocumentVersion,
  formatFileSize,
} from '../../Interfaces/ILegalClientDocumentVersion';
import { useDownloadDocument } from '../../hooks/reactQuerys/DocumentQueries';
import { DocumentVersionPreviewDialog } from './DocumentVersionPreviewDialog';

interface IProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  typeEntry: ILegalClientDocumentTypeEntry | null;
  /**
   * Entfernt genau diese Version — auch eine ältere aus der Mitte. Entfällt im
   * Nur-Lese-Modus.
   */
  onDeleteVersion?: (
    typeEntry: ILegalClientDocumentTypeEntry,
    version: ILegalClientDocumentVersion
  ) => void;
  /**
   * Nur ansehen, nicht ändern. Gebraucht dort, wo die Historie eines Mandanten
   * eingeblendet wird, ohne dass der Mandant gerade bearbeitet wird — etwa im
   * Ident-Service-Panel eines Vorgangs. Gelöscht wird eine Unterlage
   * ausschließlich beim Mandanten selbst.
   */
  isReadOnly?: boolean;
}

const formatDateTime = (value: string | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime())
    ? ''
    : parsed.toLocaleString('de-DE', {
        dateStyle: 'short',
        timeStyle: 'short',
      });
};

const formatDate = (value: string | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime())
    ? ''
    : parsed.toLocaleDateString('de-DE');
};

const userLabel = (version: ILegalClientDocumentVersion): string => {
  const name = [version.UserFirstName, version.UserLastName]
    .filter(part => part !== null && part !== undefined && part !== '')
    .join(' ');

  return name === '' ? 'System / Mandantenportal' : name;
};

/**
 * Alle Stände einer Dokumentart. Die Tabelle des Mandanten zeigt bewusst nur die
 * aktuelle Version; wer einen älteren Stand ansehen, herunterladen oder
 * verwerfen will, tut das hier.
 *
 * Löschen ist absichtlich je Zeile möglich und nicht nur am obersten Stand: eine
 * versehentlich hochgeladene Zwischenversion ließ sich sonst nur entfernen,
 * indem man alle jüngeren mit verwirft. Die Versionsnummern der übrigen Stände
 * bleiben unverändert — die Historie soll nachvollziehbar bleiben, auch mit
 * einer Lücke. Wirksam wird alles erst mit dem Speichern des Mandanten.
 */
export const DocumentVersionHistoryDialog: React.FC<IProps> = props => {
  const { downloadDocument, isPending: isDownloading } = useDownloadDocument();

  const [previewVersion, setPreviewVersion] =
    useState<ILegalClientDocumentVersion | null>(null);

  const versionArray = props.typeEntry?.VersionArray ?? [];

  const handleDelete = (version: ILegalClientDocumentVersion) => {
    if (props.typeEntry === null || props.onDeleteVersion === undefined) {
      return;
    }

    props.onDeleteVersion(props.typeEntry, version);

    // Die zuletzt verbliebene Version zu löschen entfernt das Dokument als
    // Ganzes — dann hat dieser Dialog keinen Inhalt mehr.
    if (versionArray.length <= 1) {
      props.setIsOpen(false);
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => props.setIsOpen(false)}
      maxWidth='lg'
      fullWidth
    >
      <DialogTitle>
        {props.typeEntry?.LegalClientDocumentType}
        <Typography variant='body2' color='text.secondary'>
          {versionArray.length === 1
            ? 'Ein Stand'
            : `${versionArray.length} Stände`}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <DocumentVersionPreviewDialog
          isOpen={previewVersion !== null}
          setIsOpen={(isOpen: boolean) => {
            if (!isOpen) {
              setPreviewVersion(null);
            }
          }}
          version={previewVersion ?? undefined}
          typeName={props.typeEntry?.LegalClientDocumentType ?? ''}
        />

        {versionArray.length === 0 ? (
          <Alert severity='info'>
            Zu dieser Dokumentart liegt kein Stand vor.
          </Alert>
        ) : (
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell width={130}>Version</TableCell>
                <TableCell width={170}>Hochgeladen</TableCell>
                <TableCell>Dateien</TableCell>
                <TableCell width={110}>Gültig bis</TableCell>
                <TableCell width={200}>Anmerkung</TableCell>
                <TableCell width={130} align='right'>
                  Aktionen
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {versionArray.map(version => {
                const isUnsaved =
                  (version.idLegalClientDocumentVersion ?? 0) < 0;
                const downloadableArray = version.FileArray.filter(
                  file => file.idDocument > 0
                );

                return (
                  <TableRow
                    key={`history-${version.idLegalClientDocumentVersion}`}
                    hover
                  >
                    <TableCell>
                      <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
                      >
                        <Typography variant='body2'>
                          Version {version.VersionNumber}
                        </Typography>
                        {version.isCurrent && (
                          <Chip size='small' color='success' label='aktuell' />
                        )}
                        {isUnsaved && (
                          <Chip
                            size='small'
                            color='info'
                            label='noch nicht gespeichert'
                          />
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell>
                      {formatDateTime(version.Created_at)}
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        display='block'
                      >
                        {userLabel(version)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {version.FileArray.map(file => (
                        <Typography
                          key={`history-file-${file.idDocument}`}
                          variant='body2'
                        >
                          {file.Label !== null &&
                          file.Label !== undefined &&
                          file.Label !== ''
                            ? `${file.Label}: ${file.FileName}`
                            : file.FileName}
                          {formatFileSize(file.FileSize) !== '' &&
                            ` (${formatFileSize(file.FileSize)})`}
                        </Typography>
                      ))}
                    </TableCell>

                    <TableCell>{formatDate(version.ValidUntil)}</TableCell>

                    <TableCell>
                      <Typography variant='body2' color='text.secondary'>
                        {version.Note ?? ''}
                      </Typography>
                    </TableCell>

                    <TableCell align='right'>
                      <Tooltip title='Vorschau'>
                        <span>
                          <IconButton
                            size='small'
                            disabled={version.FileArray.length === 0}
                            onClick={() => setPreviewVersion(version)}
                          >
                            <FindInPage fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          downloadableArray.length > 1
                            ? 'Alle Dateien herunterladen'
                            : 'Herunterladen'
                        }
                      >
                        <span>
                          <IconButton
                            size='small'
                            disabled={
                              isDownloading || downloadableArray.length === 0
                            }
                            onClick={() =>
                              downloadableArray.forEach(file =>
                                downloadDocument(file.idDocument)
                              )
                            }
                          >
                            <Download fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {props.isReadOnly !== true && (
                        <Tooltip
                          title={
                            versionArray.length > 1
                              ? 'Diesen Stand löschen'
                              : 'Dokument löschen'
                          }
                        >
                          <IconButton
                            size='small'
                            onClick={() => handleDelete(version)}
                          >
                            <Delete fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant='contained' onClick={() => props.setIsOpen(false)}>
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
};
